/**
 * The one zod configuration (zod.dev/error-customization): every default
 * message becomes a `validation:*` token, translated where it is shown.
 *
 * Loaded once per runtime: apps/web/instrumentation.ts (server, Node and
 * edge), apps/web/instrumentation-client.ts (browser) and the vitest setups.
 * A schema that names its own message keeps it: a check-level or
 * schema-level error takes precedence over this map.
 */
import { z } from 'zod';

import { formatValidationMessage } from './validation-message';

type Bound = number | bigint | Date;

function toNumber(value: Bound): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  return null;
}

const ID_FORMATS = new Set(['guid', 'uuid', 'cuid', 'cuid2', 'ulid', 'nanoid']);
const DATE_FORMATS = new Set(['datetime', 'date', 'time', 'duration']);

export const validationErrorMap: z.core.$ZodErrorMap = (issue) => {
  switch (issue.code) {
    case 'invalid_type':
      return issue.input === undefined || issue.input === null
        ? 'validation:required'
        : 'validation:invalidType';

    case 'too_small': {
      if (issue.origin === 'date') return 'validation:invalidDate';
      const minimum = toNumber(issue.minimum as Bound);
      if (minimum === null) return 'validation:invalid';
      if (issue.origin === 'string') {
        return minimum <= 1
          ? 'validation:required'
          : formatValidationMessage('validation:tooSmall.string', { minimum });
      }
      if (issue.origin === 'array' || issue.origin === 'set') {
        return formatValidationMessage('validation:tooSmall.array', {
          minimum,
        });
      }
      // gt(n) is exclusive: "at least n" would be wrong, so only gt(0) has a message.
      if (issue.inclusive === false) {
        return minimum === 0 ? 'validation:positive' : 'validation:invalid';
      }
      return formatValidationMessage('validation:tooSmall.number', { minimum });
    }

    case 'too_big': {
      if (issue.origin === 'date') return 'validation:invalidDate';
      const maximum = toNumber(issue.maximum as Bound);
      if (maximum === null) return 'validation:invalid';
      if (issue.origin === 'string') {
        return formatValidationMessage('validation:tooBig.string', { maximum });
      }
      if (issue.origin === 'array' || issue.origin === 'set') {
        return formatValidationMessage('validation:tooBig.array', { maximum });
      }
      if (issue.inclusive === false) return 'validation:invalid';
      return formatValidationMessage('validation:tooBig.number', { maximum });
    }

    case 'invalid_format':
      if (issue.format === 'email') return 'validation:invalidEmail';
      if (issue.format === 'url') return 'validation:invalidUrl';
      if (ID_FORMATS.has(issue.format)) return 'validation:invalidId';
      if (DATE_FORMATS.has(issue.format)) return 'validation:invalidDate';
      return 'validation:invalidFormat';

    case 'invalid_value':
      return 'validation:invalidChoice';

    default:
      return 'validation:invalid';
  }
};

let configured = false;

export function configureZod(): void {
  if (configured) return;
  z.config({ customError: validationErrorMap });
  configured = true;
}
